"use client"

import React, { useEffect, useState } from "react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Loader2, Plus, Trash2 } from "lucide-react"
import type { AtsSourceInfo } from "@/hooks/use-ats-sources"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { getArray, getPath, getString, setPath } from "./doc-paths"
import { KeyValueListEditor, NumberListEditor, StringListEditor } from "./list-editors"

const EVENT_TYPES = ["INSERTED", "UPDATED", "DELETED"]

/** Filter mode values are the processor's enum; the labels are copy, translated at render. */
const CLIENT_FILTER_MODES: { value: string; labelKey: TranslationKey }[] = [
  { value: "0", labelKey: "eventSync.editor.clientFilter.disabled" },
  { value: "1", labelKey: "eventSync.editor.clientFilter.allowlist" },
  { value: "2", labelKey: "eventSync.editor.clientFilter.denylist" },
]

const STATUS_GROUPS: { path: string; labelKey: TranslationKey; numeric: boolean }[] = [
  { path: "workOrderStatuses", labelKey: "eventSync.editor.statusMappings.workOrderStatuses", numeric: true },
  { path: "organizationStatuses", labelKey: "eventSync.editor.statusMappings.organizationStatuses", numeric: true },
  { path: "profileStatuses", labelKey: "eventSync.editor.statusMappings.profileStatuses", numeric: false },
]

/** The numeric assignment-default fields, in the order they appear in the grid. */
const ASSIGNMENT_DEFAULT_FIELDS: { field: string; labelKey: TranslationKey }[] = [
  { field: "assignedToUserProfileId", labelKey: "eventSync.editor.assignmentDefaults.assignedToUserProfileId" },
  { field: "jobCategoryId", labelKey: "eventSync.editor.assignmentDefaults.jobCategoryId" },
  {
    field: "internalOrganizationDefinition1Id",
    labelKey: "eventSync.editor.assignmentDefaults.internalOrganizationDefinition1Id",
  },
  { field: "organizationIdInternal", labelKey: "eventSync.editor.assignmentDefaults.organizationIdInternal" },
  { field: "currencyId", labelKey: "eventSync.editor.assignmentDefaults.currencyId" },
  { field: "billingInvoiceTemplateId", labelKey: "eventSync.editor.assignmentDefaults.billingInvoiceTemplateId" },
  { field: "paymentInvoiceTermsId", labelKey: "eventSync.editor.assignmentDefaults.paymentInvoiceTermsId" },
  { field: "paymentReleaseScheduleId", labelKey: "eventSync.editor.assignmentDefaults.paymentReleaseScheduleId" },
  { field: "timeSheetMethodologyId", labelKey: "eventSync.editor.assignmentDefaults.timesheetMethodologyId" },
  { field: "timeSheetCycleId", labelKey: "eventSync.editor.assignmentDefaults.timesheetCycleId" },
  {
    field: "jobOwnerCommissionRateHeaderId",
    labelKey: "eventSync.editor.assignmentDefaults.jobOwnerCommissionRateHeaderId",
  },
  { field: "jobFunctionId", labelKey: "eventSync.editor.assignmentDefaults.jobFunctionId" },
  { field: "workerLocationTypeId", labelKey: "eventSync.editor.assignmentDefaults.workerLocationTypeId" },
  { field: "jobOwnerUserProfileId", labelKey: "eventSync.editor.assignmentDefaults.jobOwnerUserProfileId" },
]

/** The boolean switches on the Source & Events tab, in render order. */
const SOURCE_SWITCHES: { field: string; labelKey: TranslationKey }[] = [
  { field: "enableManualPlacementReview", labelKey: "eventSync.editor.switches.manualPlacementReview" },
  { field: "useWorkerNameAsOrganization", labelKey: "eventSync.editor.switches.useWorkerNameAsOrganization" },
  { field: "createDraftOrganizationOnly", labelKey: "eventSync.editor.switches.createDraftOrganizationOnly" },
  { field: "requiresVendorNameExtraction", labelKey: "eventSync.editor.switches.requiresVendorNameExtraction" },
  { field: "autoCreateSupplierContact", labelKey: "eventSync.editor.switches.autoCreateSupplierContact" },
]

interface EventSyncEditorProps {
  initialDoc: Record<string, any>
  isNew: boolean
  sources: AtsSourceInfo[]
  activeSourceCode: string | null
  isSaving: boolean
  onSave: (doc: Record<string, any>) => void
  onCancel: () => void
}

/**
 * Fully structured editor over one AtsVendorConfiguration document. Every form
 * control reads/writes the document through case-insensitive path helpers, so
 * legacy property casings and unknown properties round-trip untouched. The JSON
 * tab is two-way synced with the form — paste a whole document there to copy or
 * override a configuration.
 */
export function EventSyncEditor({
  initialDoc,
  isNew,
  sources,
  activeSourceCode,
  isSaving,
  onSave,
  onCancel,
}: EventSyncEditorProps) {
  const { t } = useTranslation()
  const [doc, setDoc] = useState<Record<string, any>>(initialDoc)

  // JSON tab draft: regenerated from the form state until the admin starts typing
  const [jsonDraft, setJsonDraft] = useState<string>(() => JSON.stringify(initialDoc, null, 2))
  const [jsonDirty, setJsonDirty] = useState(false)
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    if (!jsonDirty) {
      setJsonDraft(JSON.stringify(doc, null, 2))
    }
  }, [doc, jsonDirty])

  const update = (path: string[], value: any) => setDoc((current) => setPath(current, path, value))

  // getString/getArray normalize hand-pasted JSON values: the document can hold
  // anything after an Apply on the JSON tab, and a render crash here would lose
  // every unsaved edit (the MFE has no error boundary)
  const atsSource = getString(doc, ["atsSource"])
  const selectedSource = sources.find((s) => s.code.toLowerCase() === atsSource.toLowerCase()) ?? null
  const boundToActive =
    activeSourceCode !== null && atsSource.toLowerCase() === activeSourceCode.toLowerCase()

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonDraft)
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setJsonError(t("eventSync.json.errors.notObject"))
        return
      }
      const parsedSource = getPath(parsed, ["atsSource"])
      if (parsedSource !== undefined && parsedSource !== null && typeof parsedSource !== "string") {
        setJsonError(t("eventSync.json.errors.atsSourceType"))
        return
      }
      const parsedEventTypes = getPath(parsed, ["atsConfiguration", "allowedEntityEventTypes"])
      if (parsedEventTypes !== undefined && parsedEventTypes !== null && !Array.isArray(parsedEventTypes)) {
        setJsonError(t("eventSync.json.errors.eventTypesType"))
        return
      }
      setDoc(parsed)
      setJsonDirty(false)
      setJsonError(null)
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : t("eventSync.json.errors.invalidJson"))
    }
  }

  const eventTypes = getArray(doc, ["atsConfiguration", "allowedEntityEventTypes"]).filter(
    (value): value is string => typeof value === "string"
  )
  const extraEventTypes = eventTypes.filter((type) => !EVENT_TYPES.includes(type.toUpperCase()))

  const clientFilter = getPath(doc, ["atsConfiguration", "clientCorporationFilter"])
  const clientFilterMode = clientFilter ? String(getPath(clientFilter, ["filterMode"]) ?? 0) : "0"
  const clientFilterClients = clientFilter ? getArray(clientFilter, ["clients"]) : []

  const lobAllowlist = getArray(doc, ["atsConfiguration", "linesOfBusinessAllowlist"])

  // Stored as a full ISO timestamp but edited as a date: the rule compares dates only, so exposing a time
  // component would imply a precision the comparison does not have.
  const syncCutoffRaw = getPath(doc, ["atsConfiguration", "syncCutoffDateTime"])
  const syncCutoffValue = typeof syncCutoffRaw === "string" ? syncCutoffRaw.slice(0, 10) : ""

  // Tri-state: null/undefined means "keep this source's historical behaviour", which is not the same as false.
  const enforceStatusesValue = getPath(doc, ["atsConfiguration", "enforceAllowedStatusesForImport"])
  const profileTypeRules = getArray(doc, ["businessRules", "profileTypeCodeRules"])
  const additionalMappings = getArray(doc, ["atsConfiguration", "additionalEntityMappings"])

  const isSeasonalValue = getPath(doc, ["businessRules", "assignmentDefaults", "isSeasonalOrPartTimeTimesheet"])
  const deliveryMethodValue = getPath(doc, ["businessRules", "assignmentDefaults", "deliveryMethodId"])

  const setClientFilterMode = (mode: string) => {
    const next = { ...(clientFilter ?? { clients: [] }) }
    update(["atsConfiguration", "clientCorporationFilter"], setPath(next, ["filterMode"], Number(mode)))
  }

  // Rows whose id was cleared (or never entered) are meaningless to the processor —
  // worse, they'd be consumed literally (an id-less allowlist entry still activates
  // allowlist mode). Dropped at save time so half-typed rows can't change filtering.
  const pruneIncompleteIdRows = (document: Record<string, any>): Record<string, any> => {
    let result = document
    const hasValidId = (row: any) => {
      const id = getPath(row, ["id"])
      return typeof id === "number" && Number.isFinite(id) && id > 0
    }

    const lobRows = getArray(result, ["atsConfiguration", "linesOfBusinessAllowlist"])
    if (lobRows.some((row) => !hasValidId(row))) {
      result = setPath(result, ["atsConfiguration", "linesOfBusinessAllowlist"], lobRows.filter(hasValidId))
    }

    const filter = getPath(result, ["atsConfiguration", "clientCorporationFilter"])
    if (filter && typeof filter === "object") {
      const clientRows = getArray(filter, ["clients"])
      if (clientRows.some((row) => !hasValidId(row))) {
        result = setPath(
          result,
          ["atsConfiguration", "clientCorporationFilter"],
          setPath(filter, ["clients"], clientRows.filter(hasValidId))
        )
      }
    }

    return result
  }

  const numberDefault = (field: string, labelKey: TranslationKey) => (
    <div className="space-y-1" key={field}>
      <Label className="text-xs">{t(labelKey)}</Label>
      <Input
        type="number"
        className="h-8 text-xs"
        value={getPath(doc, ["businessRules", "assignmentDefaults", field]) ?? ""}
        onChange={(e) =>
          update(
            ["businessRules", "assignmentDefaults", field],
            e.target.value === "" ? null : Number(e.target.value)
          )
        }
      />
    </div>
  )

  const configurationSwitch = (field: string, labelKey: TranslationKey) => (
    <label key={field} className="flex items-center gap-2 text-sm cursor-pointer">
      <Switch
        checked={getPath(doc, ["atsConfiguration", field]) === true}
        onCheckedChange={(checked) => update(["atsConfiguration", field], checked)}
      />
      {t(labelKey)}
    </label>
  )

  return (
    <div className="space-y-4">
      <Tabs defaultValue="source">
        <TabsList>
          <TabsTrigger value="source">{t("eventSync.editor.tabs.sourceEvents")}</TabsTrigger>
          <TabsTrigger value="status">{t("eventSync.editor.tabs.statusMappings")}</TabsTrigger>
          <TabsTrigger value="rules">{t("eventSync.editor.tabs.businessRules")}</TabsTrigger>
          <TabsTrigger value="json">{t("eventSync.editor.tabs.json")}</TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------ Source & Events */}
        <TabsContent value="source" className="space-y-5 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("eventSync.editor.source.label")}</Label>
              <Select value={selectedSource?.code ?? ""} onValueChange={(code) => update(["atsSource"], code)}>
                <SelectTrigger>
                  <SelectValue placeholder={t("eventSync.editor.source.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((source) => (
                    <SelectItem key={source.code} value={source.code}>
                      {source.code} ({source.atsSystem ?? "?"})
                      {source.isActive ? t("eventSync.source.activeSuffix") : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {atsSource && !selectedSource && (
                <p className="text-xs text-destructive">
                  {t("eventSync.editor.source.unknownSource", { code: atsSource })}
                </p>
              )}
              {selectedSource && (
                <p className="text-xs text-muted-foreground">
                  {t("eventSync.editor.source.stamped", {
                    id: selectedSource.id,
                    system: selectedSource.atsSystem,
                  })}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t("eventSync.editor.binding.label")}</Label>
              <div className="pt-1">
                {boundToActive ? (
                  <Badge className="bg-fbo-accent text-fbo-accent-foreground hover:bg-fbo-accent/90">
                    {t("eventSync.editor.binding.boundToActive")}
                  </Badge>
                ) : (
                  <p className="text-xs text-amber-600">
                    {activeSourceCode
                      ? t("eventSync.editor.binding.notActive", { code: activeSourceCode })
                      : t("eventSync.editor.binding.noActiveSource")}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("eventSync.editor.events.label")}</Label>
            <p className="text-xs text-muted-foreground">{t("eventSync.editor.events.help")}</p>
            <div className="flex gap-4">
              {EVENT_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={eventTypes.some((configured) => configured.toUpperCase() === type)}
                    onCheckedChange={(checked) => {
                      const without = eventTypes.filter((configured) => configured.toUpperCase() !== type)
                      update(
                        ["atsConfiguration", "allowedEntityEventTypes"],
                        checked ? [...without, type] : without
                      )
                    }}
                  />
                  {type}
                </label>
              ))}
            </div>
            {extraEventTypes.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t("eventSync.editor.events.alsoConfigured", {
                  types: extraEventTypes.join(", "),
                })}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {SOURCE_SWITCHES.map(({ field, labelKey }) => configurationSwitch(field, labelKey))}
          </div>

          {getPath(doc, ["atsConfiguration", "requiresVendorNameExtraction"]) === true && (
            <div className="space-y-1.5">
              <Label>{t("eventSync.editor.vendorPatterns.label")}</Label>
              <StringListEditor
                values={(getPath(doc, ["atsConfiguration", "vendorNameExtractionPatterns"]) as string[]) ?? []}
                onChange={(values) => update(["atsConfiguration", "vendorNameExtractionPatterns"], values)}
                placeholder={t("eventSync.editor.vendorPatterns.placeholder")}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>{t("eventSync.editor.clientFilter.label")}</Label>
            <Select value={clientFilterMode} onValueChange={setClientFilterMode}>
              <SelectTrigger className="w-96">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLIENT_FILTER_MODES.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {t(mode.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {clientFilterMode !== "0" && (
              <RowsEditor
                rows={clientFilterClients}
                columns={[
                  {
                    field: "id",
                    label: t("eventSync.editor.clientFilter.idColumn"),
                    numeric: true,
                  },
                  { field: "name", label: t("eventSync.editor.clientFilter.nameColumn") },
                ]}
                onChange={(rows) =>
                  update(
                    ["atsConfiguration", "clientCorporationFilter"],
                    setPath(clientFilter ?? { filterMode: Number(clientFilterMode) }, ["clients"], rows)
                  )
                }
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("eventSync.editor.syncCutoff.label")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("eventSync.editor.syncCutoff.helpBefore")}{" "}
              <strong>{t("eventSync.editor.syncCutoff.helpEnded")}</strong>{" "}
              {t("eventSync.editor.syncCutoff.helpAfter")}
            </p>
            <Input
              type="date"
              className="h-8 max-w-[200px] text-xs"
              value={syncCutoffValue}
              onChange={(event) =>
                update(
                  ["atsConfiguration", "syncCutoffDateTime"],
                  event.target.value ? new Date(`${event.target.value}T00:00:00Z`).toISOString() : null
                )
              }
            />
          </div>

          <div className="space-y-2">
            <Label>{t("eventSync.editor.enforceStatuses.label")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("eventSync.editor.enforceStatuses.help")}
            </p>
            <Select
              value={
                enforceStatusesValue === true ? "yes" : enforceStatusesValue === false ? "no" : "unset"
              }
              onValueChange={(value) =>
                update(
                  ["atsConfiguration", "enforceAllowedStatusesForImport"],
                  value === "unset" ? null : value === "yes"
                )
              }
            >
              <SelectTrigger className="h-8 max-w-[200px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">{t("eventSync.editor.enforceStatuses.default")}</SelectItem>
                <SelectItem value="yes">{t("eventSync.editor.enforceStatuses.always")}</SelectItem>
                <SelectItem value="no">{t("eventSync.editor.enforceStatuses.never")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("eventSync.editor.lobAllowlist.label")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("eventSync.editor.lobAllowlist.help")}
            </p>
            <div className="space-y-1.5">
              {lobAllowlist.map((row, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Input
                    type="number"
                    className="h-8 text-xs w-32"
                    value={getPath(row, ["id"]) ?? ""}
                    onChange={(e) => {
                      const rows = [...lobAllowlist]
                      // Cleared field stays null (not Number("") === 0 — id 0 would be
                      // saved as a real allowlist entry matching nothing); incomplete
                      // rows are pruned on save
                      rows[index] = setPath(row, ["id"], e.target.value === "" ? null : Number(e.target.value))
                      update(["atsConfiguration", "linesOfBusinessAllowlist"], rows)
                    }}
                  />
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Switch
                      checked={getPath(row, ["createWorkOrders"]) !== false}
                      onCheckedChange={(checked) => {
                        const rows = [...lobAllowlist]
                        rows[index] = setPath(row, ["createWorkOrders"], checked)
                        update(["atsConfiguration", "linesOfBusinessAllowlist"], rows)
                      }}
                    />
                    {t("eventSync.editor.lobAllowlist.createWorkOrders")}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      update(
                        ["atsConfiguration", "linesOfBusinessAllowlist"],
                        lobAllowlist.filter((_, i) => i !== index)
                      )
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  update(["atsConfiguration", "linesOfBusinessAllowlist"], [
                    ...lobAllowlist,
                    { id: null, createWorkOrders: true },
                  ])
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("eventSync.editor.lobAllowlist.add")}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("eventSync.editor.additionalMappings.label")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("eventSync.editor.additionalMappings.help")}
            </p>
            <AdditionalMappingsEditor
              mappings={additionalMappings}
              onChange={(rows) => update(["atsConfiguration", "additionalEntityMappings"], rows)}
            />
          </div>
        </TabsContent>

        {/* ------------------------------------------------ Status Mappings */}
        <TabsContent value="status" className="space-y-5 pt-2">
          <p className="text-sm text-muted-foreground">
            {t("eventSync.editor.statusMappings.intro")}
          </p>
          {STATUS_GROUPS.map((group) => {
            const groupLabel = t(group.labelKey)
            return (
              <div key={group.path} className="grid grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <Label>
                    {t("eventSync.editor.statusMappings.active", { group: groupLabel })}
                  </Label>
                  {group.numeric ? (
                    <NumberListEditor
                      values={(getPath(doc, ["statusMappings", group.path, "active"]) as number[]) ?? []}
                      onChange={(values) => update(["statusMappings", group.path, "active"], values)}
                      placeholder={t("eventSync.editor.statusMappings.statusIdPlaceholder")}
                    />
                  ) : (
                    <StringListEditor
                      values={(getPath(doc, ["statusMappings", group.path, "active"]) as string[]) ?? []}
                      onChange={(values) => update(["statusMappings", group.path, "active"], values)}
                      placeholder={t("eventSync.editor.statusMappings.statusPlaceholder")}
                    />
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>
                    {t("eventSync.editor.statusMappings.inactive", { group: groupLabel })}
                  </Label>
                  {group.numeric ? (
                    <NumberListEditor
                      values={(getPath(doc, ["statusMappings", group.path, "inactive"]) as number[]) ?? []}
                      onChange={(values) => update(["statusMappings", group.path, "inactive"], values)}
                      placeholder={t("eventSync.editor.statusMappings.statusIdPlaceholder")}
                    />
                  ) : (
                    <StringListEditor
                      values={(getPath(doc, ["statusMappings", group.path, "inactive"]) as string[]) ?? []}
                      onChange={(values) => update(["statusMappings", group.path, "inactive"], values)}
                      placeholder={t("eventSync.editor.statusMappings.statusPlaceholder")}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </TabsContent>

        {/* ------------------------------------------------ Business Rules */}
        <TabsContent value="rules" className="pt-2">
          <Accordion type="multiple" className="w-full">
            <AccordionItem value="clientOrganizations">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.clientOrganizations.title")}
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={
                      getPath(doc, [
                        "businessRules",
                        "clientOrganizations",
                        "defaults",
                        "useCountryOfRegistrationFromAddress",
                      ]) === true
                    }
                    onCheckedChange={(checked) =>
                      update(
                        ["businessRules", "clientOrganizations", "defaults", "useCountryOfRegistrationFromAddress"],
                        checked
                      )
                    }
                  />
                  {t("eventSync.editor.clientOrganizations.useCountryOfRegistration")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={
                      getPath(doc, [
                        "businessRules",
                        "clientOrganizations",
                        "defaults",
                        "useDefaultTaxProvinceFromAddress",
                      ]) === true
                    }
                    onCheckedChange={(checked) =>
                      update(
                        ["businessRules", "clientOrganizations", "defaults", "useDefaultTaxProvinceFromAddress"],
                        checked
                      )
                    }
                  />
                  {t("eventSync.editor.clientOrganizations.useDefaultTaxProvince")}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("eventSync.editor.clientOrganizations.defaultOrganizationTypeCode")}
                    </Label>
                    <Input
                      className="h-8 text-xs"
                      placeholder={t(
                        "eventSync.editor.clientOrganizations.defaultOrganizationTypeCodePlaceholder"
                      )}
                      value={
                        getPath(doc, ["businessRules", "clientOrganizations", "defaults", "defaultOrganizationTypeCode"]) ??
                        ""
                      }
                      onChange={(e) =>
                        update(
                          ["businessRules", "clientOrganizations", "defaults", "defaultOrganizationTypeCode"],
                          e.target.value || null
                        )
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("eventSync.editor.clientOrganizations.defaultSalesTaxTerritoryCode")}
                    </Label>
                    <Input
                      className="h-8 text-xs"
                      placeholder={t(
                        "eventSync.editor.clientOrganizations.defaultSalesTaxTerritoryCodePlaceholder"
                      )}
                      value={
                        getPath(doc, ["businessRules", "clientOrganizations", "defaults", "defaultSalesTaxTerritoryCode"]) ??
                        ""
                      }
                      onChange={(e) =>
                        update(
                          ["businessRules", "clientOrganizations", "defaults", "defaultSalesTaxTerritoryCode"],
                          e.target.value || null
                        )
                      }
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="countryCodes">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.countryCodes.title")}
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                <KeyValueListEditor
                  entries={(getPath(doc, ["businessRules", "countryCodeMappings", "roleTypeMappings"]) as Record<string, string>) ?? {}}
                  onChange={(entries) => update(["businessRules", "countryCodeMappings", "roleTypeMappings"], entries)}
                  keyPlaceholder={t("eventSync.editor.countryCodes.roleTypePlaceholder")}
                  valuePlaceholder={t("eventSync.editor.countryCodes.countryCodePlaceholder")}
                />
                <div className="space-y-1 w-48">
                  <Label className="text-xs">
                    {t("eventSync.editor.countryCodes.defaultCountryCode")}
                  </Label>
                  <Input
                    className="h-8 text-xs"
                    value={getPath(doc, ["businessRules", "countryCodeMappings", "defaultCountryCode"]) ?? ""}
                    onChange={(e) => update(["businessRules", "countryCodeMappings", "defaultCountryCode"], e.target.value)}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="roleIds">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.roleIds.title")}
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                <KeyValueListEditor
                  entries={(getPath(doc, ["businessRules", "roleIdMappings", "roleTypeToRoleId"]) as Record<string, number>) ?? {}}
                  onChange={(entries) => update(["businessRules", "roleIdMappings", "roleTypeToRoleId"], entries)}
                  keyPlaceholder={t("eventSync.editor.countryCodes.roleTypePlaceholder")}
                  valuePlaceholder={t("eventSync.editor.roleIds.roleIdPlaceholder")}
                  numericValues
                />
                <div className="space-y-1 w-48">
                  <Label className="text-xs">{t("eventSync.editor.roleIds.defaultRoleId")}</Label>
                  <Input
                    type="number"
                    className="h-8 text-xs"
                    value={getPath(doc, ["businessRules", "roleIdMappings", "defaultRoleId"]) ?? ""}
                    onChange={(e) =>
                      update(["businessRules", "roleIdMappings", "defaultRoleId"], Number(e.target.value || 0))
                    }
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="placementTypes">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.placementTypes.title")}
              </AccordionTrigger>
              <AccordionContent className="grid grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("eventSync.editor.placementTypes.skipped")}</Label>
                  <StringListEditor
                    values={(getPath(doc, ["businessRules", "placementTypeMappings", "skippedPlacementTypes"]) as string[]) ?? []}
                    onChange={(values) => update(["businessRules", "placementTypeMappings", "skippedPlacementTypes"], values)}
                    placeholder={t("eventSync.editor.placementTypes.placeholder")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("eventSync.editor.placementTypes.permanent")}</Label>
                  <StringListEditor
                    values={(getPath(doc, ["businessRules", "placementTypeMappings", "permanentPlacementTypes"]) as string[]) ?? []}
                    onChange={(values) => update(["businessRules", "placementTypeMappings", "permanentPlacementTypes"], values)}
                    placeholder={t("eventSync.editor.placementTypes.permanentPlaceholder")}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="profileTypeRules">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.profileTypeRules.title")}
              </AccordionTrigger>
              <AccordionContent>
                <RowsEditor
                  rows={profileTypeRules}
                  columns={[
                    {
                      field: "placementType",
                      label: t("eventSync.editor.profileTypeRules.placementType"),
                    },
                    {
                      field: "workerLocationCountry",
                      label: t("eventSync.editor.profileTypeRules.workerCountry"),
                    },
                    {
                      field: "profileTypeCode",
                      label: t("eventSync.editor.profileTypeRules.profileTypeCode"),
                    },
                  ]}
                  onChange={(rows) => update(["businessRules", "profileTypeCodeRules"], rows)}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="subdivisions">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.subdivisions.title")}
              </AccordionTrigger>
              <AccordionContent>
                <KeyValueListEditor
                  entries={(getPath(doc, ["businessRules", "subdivisionMappings", "mappings"]) as Record<string, string>) ?? {}}
                  onChange={(entries) => update(["businessRules", "subdivisionMappings", "mappings"], entries)}
                  keyPlaceholder={t("eventSync.editor.subdivisions.keyPlaceholder")}
                  valuePlaceholder={t("eventSync.editor.subdivisions.valuePlaceholder")}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="assignmentDefaults">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.assignmentDefaults.title")}
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {t("eventSync.editor.assignmentDefaults.help")}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {ASSIGNMENT_DEFAULT_FIELDS.map(({ field, labelKey }) => numberDefault(field, labelKey))}
                  <div className="space-y-1">
                    {/* A Select rather than a number field: the constraint is an enum SUBSET, not a range.
                        Only the self-contained DeliveryMethod values are usable as a default — the other three
                        (internal profile, client profile, file export) each need a companion field no default
                        supplies, and Phoenix logs-and-skips them during work-order creation, which no admin
                        reads. Offering only the three valid ones makes that unreachable. */}
                    <Label className="text-xs">
                      {t("eventSync.editor.assignmentDefaults.deliveryMethod")}
                    </Label>
                    <Select
                      value={deliveryMethodValue == null ? "unset" : String(deliveryMethodValue)}
                      onValueChange={(value) =>
                        update(
                          ["businessRules", "assignmentDefaults", "deliveryMethodId"],
                          value === "unset" ? null : Number(value)
                        )
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">
                          {t("eventSync.editor.assignmentDefaults.notSet")}
                        </SelectItem>
                        <SelectItem value="1">
                          {t("eventSync.editor.assignmentDefaults.deliveryMethodHardCopy")}
                        </SelectItem>
                        <SelectItem value="2">
                          {t("eventSync.editor.assignmentDefaults.deliveryMethodSoftCopy")}
                        </SelectItem>
                        <SelectItem value="3">
                          {t("eventSync.editor.assignmentDefaults.deliveryMethodSuppressed")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("eventSync.editor.assignmentDefaults.seasonalTimesheet")}
                    </Label>
                    <Select
                      value={isSeasonalValue === true ? "yes" : isSeasonalValue === false ? "no" : "unset"}
                      onValueChange={(value) =>
                        update(
                          ["businessRules", "assignmentDefaults", "isSeasonalOrPartTimeTimesheet"],
                          value === "unset" ? null : value === "yes"
                        )
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">
                          {t("eventSync.editor.assignmentDefaults.notSet")}
                        </SelectItem>
                        <SelectItem value="yes">{t("common.state.yes")}</SelectItem>
                        <SelectItem value="no">{t("common.state.no")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="templates">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.templates.title")}
              </AccordionTrigger>
              <AccordionContent>
                <KeyValueListEditor
                  entries={(getPath(doc, ["businessRules", "workOrderTemplateMappings", "workerTypeToTemplateId"]) as Record<string, number>) ?? {}}
                  onChange={(entries) => update(["businessRules", "workOrderTemplateMappings", "workerTypeToTemplateId"], entries)}
                  keyPlaceholder={t("eventSync.editor.templates.workerTypePlaceholder")}
                  valuePlaceholder={t("eventSync.editor.templates.templateIdPlaceholder")}
                  numericValues
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="lob">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.lob.title")}
              </AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("eventSync.editor.lob.codeToId")}</Label>
                  <KeyValueListEditor
                    entries={(getPath(doc, ["businessRules", "lineOfBusinessIdMappings", "lineOfBusinessCodeToId"]) as Record<string, number>) ?? {}}
                    onChange={(entries) => update(["businessRules", "lineOfBusinessIdMappings", "lineOfBusinessCodeToId"], entries)}
                    keyPlaceholder={t("eventSync.editor.lob.codePlaceholder")}
                    valuePlaceholder={t("eventSync.editor.lob.idPlaceholder")}
                    numericValues
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("eventSync.editor.lob.locationTypeToId")}</Label>
                  <KeyValueListEditor
                    entries={(getPath(doc, ["businessRules", "workerLocationTypeMappings", "workerLocationTypeCodeToId"]) as Record<string, number>) ?? {}}
                    onChange={(entries) => update(["businessRules", "workerLocationTypeMappings", "workerLocationTypeCodeToId"], entries)}
                    keyPlaceholder={t("eventSync.editor.lob.locationTypeCodePlaceholder")}
                    valuePlaceholder={t("eventSync.editor.lob.locationTypeIdPlaceholder")}
                    numericValues
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("eventSync.editor.lob.engagementEligible")}</Label>
                  <NumberListEditor
                    values={(getPath(doc, ["businessRules", "engagementEligibleLinesOfBusinessIds"]) as number[]) ?? []}
                    onChange={(values) => update(["businessRules", "engagementEligibleLinesOfBusinessIds"], values)}
                    placeholder={t("eventSync.editor.lob.idPlaceholder")}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="paymentTerms">
              <AccordionTrigger className="text-sm">
                {t("eventSync.editor.paymentTerms.title")}
              </AccordionTrigger>
              <AccordionContent className="space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  {t("eventSync.editor.paymentTerms.help")}
                </p>
                <StringListEditor
                  values={(getPath(doc, ["businessRules", "restrictPaymentTermsToWorkerTypes"]) as string[]) ?? []}
                  onChange={(values) => update(["businessRules", "restrictPaymentTermsToWorkerTypes"], values)}
                  placeholder={t("eventSync.editor.templates.workerTypePlaceholder")}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>

        {/* ------------------------------------------------ JSON */}
        <TabsContent value="json" className="space-y-3 pt-2">
          <p className="text-sm text-muted-foreground">{t("eventSync.json.help")}</p>
          <Textarea
            className="font-mono text-xs min-h-96"
            value={jsonDraft}
            onChange={(e) => {
              setJsonDraft(e.target.value)
              setJsonDirty(true)
              setJsonError(null)
            }}
          />
          {jsonError && (
            <Alert variant="destructive">
              <AlertDescription>{jsonError}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={applyJson} disabled={!jsonDirty}>
              {t("eventSync.json.applyToForm")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setJsonDraft(JSON.stringify(doc, null, 2))
                setJsonDirty(false)
                setJsonError(null)
              }}
              disabled={!jsonDirty}
            >
              {t("eventSync.json.discard")}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          {t("common.actions.cancel")}
        </Button>
        <Button
          onClick={() => onSave(pruneIncompleteIdRows(doc))}
          disabled={isSaving || !atsSource.trim() || jsonDirty}
          title={jsonDirty ? t("eventSync.editor.saveDisabledTitle") : undefined}
        >
          {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {t((isNew ? "eventSync.editor.create" : "eventSync.editor.save"))}
        </Button>
      </div>
      {jsonDirty && (
        <p className="text-xs text-amber-600 text-right">{t("eventSync.json.unapplied")}</p>
      )}
    </div>
  )
}

/** Generic row editor for small arrays of flat objects (id/name pairs, rule rows). */
function RowsEditor({
  rows,
  columns,
  onChange,
}: {
  rows: any[]
  columns: { field: string; label: string; numeric?: boolean }[]
  onChange: (rows: any[]) => void
}) {
  const { t } = useTranslation()

  const emptyRow = () =>
    columns.reduce<Record<string, any>>((row, column) => {
      row[column.field] = column.numeric ? null : ""
      return row
    }, {})

  return (
    <div className="space-y-1.5">
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          {columns.map((column) => (
            <Input
              key={column.field}
              type={column.numeric ? "number" : "text"}
              className="h-8 text-xs"
              placeholder={column.label}
              value={getPath(row, [column.field]) ?? ""}
              onChange={(e) => {
                const next = [...rows]
                // Cleared numeric fields stay null instead of Number("") === 0
                const value = column.numeric ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value
                next[index] = setPath(row, [column.field], value)
                onChange(next)
              }}
            />
          ))}
          <Button variant="ghost" size="sm" onClick={() => onChange(rows.filter((_, i) => i !== index))}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...rows, emptyRow()])}>
        <Plus className="h-3.5 w-3.5 mr-1" /> {t("eventSync.editor.rows.add")}
      </Button>
    </div>
  )
}

/** Entity rows with nested source→destination field mapping rows. */
function AdditionalMappingsEditor({
  mappings,
  onChange,
}: {
  mappings: any[]
  onChange: (mappings: any[]) => void
}) {
  const { t } = useTranslation()

  const updateRow = (index: number, row: any) => {
    const next = [...mappings]
    next[index] = row
    onChange(next)
  }

  return (
    <div className="space-y-3">
      {mappings.map((mapping, index) => {
        const fieldMappings = getArray(mapping, ["fieldMappings"])
        return (
          <div key={index} className="rounded-md border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                className="h-8 text-xs w-48"
                placeholder={t("eventSync.editor.additionalMappings.entityTypePlaceholder")}
                value={getPath(mapping, ["entityType"]) ?? ""}
                onChange={(e) => updateRow(index, setPath(mapping, ["entityType"], e.target.value))}
              />
              <Input
                className="h-8 text-xs w-48"
                placeholder={t("eventSync.editor.additionalMappings.namePlaceholder")}
                value={getPath(mapping, ["name"]) ?? ""}
                onChange={(e) => updateRow(index, setPath(mapping, ["name"], e.target.value))}
              />
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => onChange(mappings.filter((_, i) => i !== index))}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-1.5 pl-2">
              {fieldMappings.map((fieldMapping, fieldIndex) => (
                <div key={fieldIndex} className="flex items-center gap-2">
                  <Input
                    className="h-8 text-xs font-mono"
                    placeholder={t("eventSync.editor.additionalMappings.sourceFieldPlaceholder")}
                    value={getPath(fieldMapping, ["sourceField"]) ?? ""}
                    onChange={(e) => {
                      const nextFields = [...fieldMappings]
                      nextFields[fieldIndex] = setPath(fieldMapping, ["sourceField"], e.target.value)
                      updateRow(index, setPath(mapping, ["fieldMappings"], nextFields))
                    }}
                  />
                  <Input
                    className="h-8 text-xs font-mono"
                    placeholder={t("eventSync.editor.additionalMappings.destinationFieldPlaceholder")}
                    value={getPath(fieldMapping, ["destinationField"]) ?? ""}
                    onChange={(e) => {
                      const nextFields = [...fieldMappings]
                      nextFields[fieldIndex] = setPath(fieldMapping, ["destinationField"], e.target.value)
                      updateRow(index, setPath(mapping, ["fieldMappings"], nextFields))
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      updateRow(
                        index,
                        setPath(mapping, ["fieldMappings"], fieldMappings.filter((_, i) => i !== fieldIndex))
                      )
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  updateRow(
                    index,
                    setPath(mapping, ["fieldMappings"], [...fieldMappings, { sourceField: "", destinationField: "" }])
                  )
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" />{" "}
                {t("eventSync.editor.additionalMappings.addFieldMapping")}
              </Button>
            </div>
          </div>
        )
      })}
      <Button variant="outline" size="sm" onClick={() => onChange([...mappings, { entityType: "", name: "", fieldMappings: [] }])}>
        <Plus className="h-3.5 w-3.5 mr-1" />{" "}
        {t("eventSync.editor.additionalMappings.addEntityMapping")}
      </Button>
    </div>
  )
}
